// LocaleSwitcher — the public-page language switcher (issue 443). Before this,
// the language mechanism (persisted `fexo.locale`, lazy es/pt catalog chunks,
// <html lang> sync, no-reload remount) only had a UI hook inside the
// authenticated shell's AccountMenu — a signed-out user on /login, /recovery,
// /auth/verify, or /invite had no control at all. Every part of the mechanism
// already worked pre-login (app.ts wires the LOCALE_CHANGED_EVENT remount
// listener unconditionally, outside the session-gated block); this just adds
// the missing control.
//
// Wired to the SAME canonical switch path as AccountMenu.ts's in-menu
// control: ensureCatalogLoaded() BEFORE setLocale({ persist: true }) so the
// remount renders in the new language, not an en flash. A failed chunk load
// still applies the switch — the UI renders through the en fallback
// (FE-006.3) rather than the click doing nothing.

import { ensureCatalogLoaded, getLocale, setLocale, SUPPORTED_LOCALES, t, type Locale, type StringKey } from '../i18n';
import { segmentedToggle } from './SegmentedToggle';

// The eager `me.locale.*` keys (frontend/src/i18n/en.ts) — the ONE label map
// every language control uses (this switcher, AccountMenu, the phone Me hub —
// issue 867), available pre-login without a lazy chunk.
export const LOCALE_LABEL: Record<Locale, StringKey> = {
  en: 'me.locale.en',
  es: 'me.locale.es',
  pt: 'me.locale.pt',
};

// switchLocale is the ONE canonical language switch: the lazy es/pt catalogue
// (issue 114) loads BEFORE setLocale({ persist: true }), so the remount that
// LOCALE_CHANGED_EVENT triggers (app.ts) renders in the new language, not an
// en flash. A failed chunk load still applies the switch — the UI renders
// through the en fallback (FE-006.3) rather than the click doing nothing.
export function switchLocale(locale: Locale): Promise<void> {
  return ensureCatalogLoaded(locale)
    .catch(() => undefined)
    .then(() => setLocale(locale, { persist: true }));
}

export function buildLocaleSwitcher(): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'locale-switcher';
  wrap.appendChild(
    segmentedToggle({
      baseClass: 'locale-switcher__segment',
      ariaLabel: t('me.language'),
      values: SUPPORTED_LOCALES,
      labelFor: (v) => t(LOCALE_LABEL[v]),
      current: getLocale(),
      onSelect: (locale) => void switchLocale(locale),
      live: false,
    }),
  );
  return wrap;
}
